import { useState } from "react";
import ComponentCard from "../../common/ComponentCard";
import Label from "../../form/Label";
import Input from "../../form/input/InputField";
import Button from "../../ui/button/Button";
import axios from "axios";
import { authService } from "../../../services/authService";
import { useNavigate } from "react-router";
import Alert from "../../ui/alert/Alert";
import DnsInstructionsModal from "./DnsInstructionsModal";

interface DomainFormData {
    domain: string;
}

interface Domain {
    id: string;
    domain: string;
    cnameRecord: string;
    verified: boolean;
}

interface NewDomainFormProps {
    onSuccess?: () => void;
    onError?: (error: any) => void;
}

export default function NewDomainForm({ onSuccess, onError }: NewDomainFormProps) {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<DomainFormData>({
        domain: ""
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [apiError, setApiError] = useState("");
    const [apiSuccess, setApiSuccess] = useState("");
    const [createdDomain, setCreatedDomain] = useState<Domain | null>(null);
    const [showDnsInstructions, setShowDnsInstructions] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;

        // Limpar o erro específico quando o campo é modificado
        if (errors[id]) {
            setErrors({
                ...errors,
                [id]: ""
            });
        }

        // Limpar mensagens de API quando o usuário faz alterações
        if (apiError) setApiError("");
        if (apiSuccess) setApiSuccess("");

        // Remover protocolo e www se o usuário digitar
        let cleanValue = value.trim().toLowerCase();
        cleanValue = cleanValue.replace(/^(https?:\/\/)?(www\.)?/, '');
        cleanValue = cleanValue.replace(/\/$/, ''); // Remove trailing slash

        setFormData({ ...formData, domain: cleanValue });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.domain) {
            newErrors.domain = "O domínio é obrigatório";
        } else {
            // Validação básica de domínio
            const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,}\.?)+$/;
            if (!domainRegex.test(formData.domain)) {
                newErrors.domain = "Digite um domínio válido (ex: seusite.com.br)";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        // Validar formulário
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setApiError("");
        setApiSuccess("");

        try {
            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/api/tracking-domains`,
                {
                    domain: formData.domain
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            if (response.data && response.data.success) {
                setApiSuccess("Domínio adicionado com sucesso!");
                setCreatedDomain(response.data.data);

                // Abrir modal com instruções DNS imediatamente
                setShowDnsInstructions(true);

                // Chamar callback de sucesso
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                const errorMsg = response.data?.message || "Erro ao adicionar domínio";
                setApiError(errorMsg);
                if (onError) {
                    onError(new Error(errorMsg));
                }
            }
        } catch (error: any) {
            console.error("Erro ao adicionar domínio:", error);
            const errorMsg = error.response?.data?.message ||
                "Erro ao adicionar domínio. Tente novamente.";
            setApiError(errorMsg);
            if (onError) {
                onError(error);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDnsInstructions = () => {
        setShowDnsInstructions(false);
        // Redirecionar para a lista de domínios
        navigate('/apps/domains');
    };

    return (
        <>
            <ComponentCard title="Adicionar Novo Domínio">
                <div className="space-y-6">
                    {apiError && (
                        <Alert
                            variant="error"
                            title="Erro"
                            message={apiError}
                        />
                    )}

                    {apiSuccess && (
                        <Alert
                            variant="success"
                            title="Sucesso"
                            message={apiSuccess}
                        />
                    )}

                    <div className="space-y-5">
                        <div>
                            <Label htmlFor="domain">Domínio *</Label>
                            <Input
                                type="text"
                                id="domain"
                                placeholder="Ex: seusite.com.br"
                                onChange={handleInputChange}
                                value={formData.domain}
                                error={!!errors.domain}
                                hint={errors.domain}
                            />
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                Digite apenas o domínio principal (sem www ou https://)
                            </p>
                            {formData.domain && (
                                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                    <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">
                                        Subdomínio de tracking:
                                    </p>
                                    <code className="text-sm text-blue-900 dark:text-blue-100 font-mono">
                                        tracking.{formData.domain}
                                    </code>
                                </div>
                            )}
                        </div>

                        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Adicione um registro CNAME no seu provedor de DNS:
                            </h4>
                            <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-500 mt-0.5">•</span>
                                    <span>Visite o painel de configurações de DNS do seu domínio</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-500 mt-0.5">•</span>
                                    <span>Crie um registro CNAME com o valor tracking.seudominio.com apontando para tracking.sellyo.com.br</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="text-blue-500 mt-0.5">•</span>
                                    <span>Após configurar, você poderá verificar o domínio</span>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex justify-end gap-4">
                        <Button
                            size="md"
                            variant="outline"
                            onClick={() => navigate('/apps/domains')}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button
                            size="md"
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? "Adicionando..." : "Adicionar Domínio"}
                        </Button>
                    </div>
                </div>
            </ComponentCard>

            {/* Modal com instruções DNS */}
            {createdDomain && (
                <DnsInstructionsModal
                    isOpen={showDnsInstructions}
                    onClose={handleCloseDnsInstructions}
                    domain={createdDomain}
                    onVerify={handleCloseDnsInstructions}
                />
            )}
        </>
    );
}
